import Link from "@components/Link/Link";
import { ProgrammeDetails } from "@customTypes/programmeDetails";
import { useSettings } from "@utilities/context/settings";
import { convertToBulletedList } from "@utilities/convertToBulletedList";
import { normaliseSlug } from "@utilities/normaliseSlug";
import clsx from "clsx";
import { format, parseISO } from "date-fns";
import styles from "./C272_ProgrammeComparisonTable.module.scss";

interface ProgrammeEEProps {
  count: number;
  programmeDetails: ProgrammeDetails;
}

export const ProgrammeEE = (props: ProgrammeEEProps) => {
  const { count, programmeDetails } = props;

  const {
    programmeSettings: {
      programmeDeliveryLabel,
      programmeDurationLabel,
      programmeFeaturesLabel,
      programmeKeyTakeAwaysLabel,
      programmeLocationLabel,
      programmeNextStartDateLabel,
      programmeFeesLabel,
      programmeTestimonialLabel,
      programmeWhoAttendsLabel,
      fallbackProgrammeIterationFee,
      fallbackProgrammeIterationDuration,
      fallbackProgrammeIterationLocation,
      programmeInfoCtaLabel,
      noProgrammeDateAvailableMessage,
    },
  } = useSettings();

  const {
    slug,
    keyTakeAway,
    programmeFeature,
    programmeTitle,
    testimonialName,
    testimonialQuote,
    whoAttends,
    programmeDeliveryText,
    programmeImportData,
  } = programmeDetails;

  const renderTestimonialName = testimonialName ? ` - ${testimonialName}` : "";
  const link = normaliseSlug(slug);
  return (
    <li className={clsx(styles["programme-column"], "programme-column")}>
      <div
        className={clsx(styles["top-heading"], "top-heading")}
        data-reference="10"
      >
        <h3>
          <Link href={link}>{programmeTitle}</Link>
        </h3>
      </div>
      <div className={styles.info}>
        {count === 1 && (
          <div className={styles["labels-wrapper"]}>
            <div className={clsx(styles.labels, "labels")}>
              <span data-reference="1" aria-hidden="true">
                <span>{programmeFeaturesLabel}</span>
              </span>
              <span data-reference="2" aria-hidden="true">
                <span>{programmeKeyTakeAwaysLabel}</span>
              </span>
              <span data-reference="3" aria-hidden="true">
                <span>{programmeWhoAttendsLabel}</span>
              </span>
              <span data-reference="4" aria-hidden="true">
                <span>{programmeDeliveryLabel}</span>
              </span>
              <span data-reference="5" aria-hidden="true">
                <span>{programmeDurationLabel}</span>
              </span>
              <span data-reference="6" aria-hidden="true">
                <span>{programmeLocationLabel}</span>
              </span>
              <span data-reference="7" aria-hidden="true">
                <span>{programmeFeesLabel}</span>
              </span>
              <span data-reference="8" aria-hidden="true">
                <span>{programmeTestimonialLabel}</span>
              </span>
              <span data-reference="9" aria-hidden="true">
                <span>{programmeNextStartDateLabel}</span>
              </span>
            </div>
          </div>
        )}
        <div data-reference="1">
          <span className={clsx(styles["inner-wrapper"], "leftside-text")}>
            <span className={styles["d-invis"]}>{programmeFeaturesLabel}</span>
            {programmeFeature}
          </span>
        </div>
        <div data-reference="2">
          <span className={clsx(styles["inner-wrapper"], "leftside-text")}>
            <span className={styles["d-invis"]}>
              {programmeKeyTakeAwaysLabel}
            </span>
            {keyTakeAway}
          </span>
        </div>
        <div data-reference="3">
          <span className={clsx(styles["inner-wrapper"], "leftside-text")}>
            <span className={styles["d-invis"]}>
              {programmeWhoAttendsLabel}
            </span>
            {whoAttends}
          </span>
        </div>
        <div data-reference="4">
          <span className={clsx(styles["inner-wrapper"], styles["rich-text"])}>
            <span className={styles["d-invis"]}>{programmeDeliveryLabel}</span>
            {convertToBulletedList(programmeDeliveryText)}
          </span>
        </div>
        <div data-reference="5">
          <span className={styles["inner-wrapper"]}>
            <span className={styles["d-invis"]}>{programmeDurationLabel}</span>
            {programmeImportData && programmeImportData[0]?.programmeDuration
              ? programmeImportData[0]?.programmeDuration
              : fallbackProgrammeIterationDuration}
          </span>
        </div>
        <div data-reference="6">
          <span className={styles["inner-wrapper"]}>
            <span className={styles["d-invis"]}>{programmeLocationLabel}</span>
            {programmeImportData && programmeImportData[0]?.programmeLocation
              ? programmeImportData[0]?.programmeLocation
              : fallbackProgrammeIterationLocation}
          </span>
        </div>
        <div data-reference="7">
          <span className={styles["inner-wrapper"]}>
            <span className={styles["d-invis"]}>{programmeFeesLabel}</span>
            {programmeImportData && programmeImportData[0]?.programmeFee
              ? programmeImportData[0]?.programmeFee
              : fallbackProgrammeIterationFee}
          </span>
        </div>
        <div data-reference="8">
          <span className={styles["inner-wrapper"]}>
            <span className={styles["d-invis"]}>
              {programmeTestimonialLabel}
            </span>
            {`"${testimonialQuote}"${renderTestimonialName}`}
          </span>
        </div>
        <div data-reference="9">
          <span className={styles["inner-wrapper"]}>
            <span className={styles["d-invis"]}>
              {programmeNextStartDateLabel}
            </span>
            {programmeImportData && programmeImportData[0]?.programmeStartDate
              ? format(
                  parseISO(programmeImportData[0].programmeStartDate),
                  "MMMM dd, yyyy"
                )
              : noProgrammeDateAvailableMessage}
          </span>
        </div>
      </div>
      <div className={styles["bottom-ctas"]}>
        <Link href={link} className="cta">
          {programmeInfoCtaLabel}
        </Link>
      </div>
    </li>
  );
};

export default ProgrammeEE;
